import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '../../i18n.ts';
import { Modal } from '../ui/Modal.tsx';

interface WhatsNewModalProps {
  version: string;
  releaseNotes: string;
  onClose: () => void;
}

/**
 * Lightweight markdown→HTML renderer for release notes.
 * Input is trusted (our own GitHub Release body, not user content).
 * Supports: ## headings, ### subheadings, **bold**, - bullet lists, #NNN issue refs.
 */
function renderReleaseNotes(md: string): string {
  return md
    .split('\n')
    .map((line) => {
      // Skip top-level title (## v1.x.x) — already shown in modal header
      if (/^## /.test(line)) return '';
      // Release date → subtle meta line
      if (/^\*\*Release datum/.test(line)) {
        const content = line.replace(/\*\*/g, '');
        return `<p class="text-xs text-pt-sage-text mb-4">${content}</p>`;
      }
      // ### Section headings → styled subheaders
      if (/^### /.test(line)) {
        const text = line.replace(/^### /, '');
        return `<h3 class="text-sm font-black text-pt-forest uppercase tracking-wider mt-5 mb-2 flex items-center gap-2"><span class="w-1 h-4 bg-pt-gold rounded-full inline-block"></span>${text}</h3>`;
      }
      // Bullet items: - **#NNN** Description
      if (/^- /.test(line)) {
        let content = line.replace(/^- /, '');
        // Issue number badges
        content = content.replace(
          /\*\*#(\d+)\*\*/g,
          '<span class="inline-flex items-center px-1.5 py-0.5 rounded-md bg-pt-gold/15 text-pt-forest font-bold text-xs mr-1">#$1</span>'
        );
        // Remaining **bold**
        content = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        return `<div class="flex items-start gap-2 mb-1.5"><span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-pt-forest/30 shrink-0"></span><span class="text-sm text-pt-forest/80 leading-relaxed">${content}</span></div>`;
      }
      // Empty lines → skip
      if (line.trim() === '') return '';
      // Fallback: plain paragraph with bold support
      let content = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      return `<p class="text-sm text-pt-forest/80 mb-2">${content}</p>`;
    })
    .join('\n');
}

const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ version, releaseNotes, onClose }) => {
  const { t } = useTranslation();
  // Content comes from our own GitHub Release body via /api/releases/latest
  // Sanitized with DOMPurify as defense-in-depth against compromised GitHub account
  const renderedNotes = useMemo(
    () =>
      DOMPurify.sanitize(renderReleaseNotes(releaseNotes || ''), { USE_PROFILES: { html: true } }),
    [releaseNotes]
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      titleId="modal-title-whatsnew"
      size="md"
      className="!max-w-lg"
    >
      {/* Header */}
      <div className="bg-pt-forest p-8 text-center">
        <Sparkles className="text-pt-gold mx-auto mb-3" size={32} />
        <h2 id="modal-title-whatsnew" className="text-white font-black text-xl">
          {t('whatsNew')} v{version}?
        </h2>
      </div>

      {/* Body — content is trusted (our own GitHub Release body) */}
      <div className="p-8 max-h-[60vh] overflow-y-auto dark:text-[var(--text-main)]">
        {releaseNotes ? (
          <div dangerouslySetInnerHTML={{ __html: renderedNotes }} />
        ) : (
          <p className="text-sm text-pt-forest/60 dark:text-[var(--text-muted)]">
            {t('noReleaseNotes')}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-pt-forest/5 dark:border-white/10 flex justify-center">
        <button
          onClick={onClose}
          className="px-8 py-3 bg-pt-gold text-pt-forest font-black rounded-full hover:bg-pt-gold/90 transition-all text-sm uppercase tracking-wider"
        >
          {t('understood')}
        </button>
      </div>
    </Modal>
  );
};

export default WhatsNewModal;
